import React, { useState, useEffect } from 'react';
import { Edit, Trash2, ChevronDown, ChevronUp, Check, X, ExternalLink, FileText, Database } from 'lucide-react';
import ArticleSubmissionForm from './ArticleSubmissionForm';
import ArticleClaimExtractor from './ArticleClaimExtractor';
import ArticleSchemaManager from './ArticleSchemaManager';
import ArticleWorkflow from './ArticleWorkflow';
import ArticleDetailView from './ArticleDetailView';

// Publisher form component
const PublisherForm = ({ initialData = null, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    credibility_rating: 50,
    bias_pattern: '',
    url: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        credibility_rating: initialData.credibility_rating || 50,
        bias_pattern: initialData.bias_pattern || '',
        url: initialData.url || ''
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'credibility_rating' ? Number(value) : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow-sm">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Publisher Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>
      
      <div>
        <label htmlFor="url" className="block text-sm font-medium text-gray-700">Publisher URL</label>
        <input
          type="url"
          id="url"
          name="url"
          value={formData.url}
          onChange={handleChange}
          placeholder="https://example.com"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>
      
      <div>
        <label htmlFor="credibility_rating" className="block text-sm font-medium text-gray-700">
          Credibility Rating (0-100)
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="range"
            id="credibility_rating"
            name="credibility_rating"
            min="0"
            max="100"
            value={formData.credibility_rating}
            onChange={handleChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-sm font-medium text-gray-700">{formData.credibility_rating}</span>
        </div>
      </div>
      
      <div>
        <label htmlFor="bias_pattern" className="block text-sm font-medium text-gray-700">Bias Pattern</label>
        <textarea
          id="bias_pattern"
          name="bias_pattern"
          value={formData.bias_pattern}
          onChange={handleChange}
          rows="3"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          placeholder="Describe any known bias patterns for this publisher..."
        />
      </div>
      
      <div className="flex justify-end space-x-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {initialData ? 'Update Publisher' : 'Add Publisher'}
        </button>
      </div>
    </form>
  );
};

// Author form component
const AuthorForm = ({ initialData = null, publishers, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    publisher_id: '',
    credibility_rating: 50,
    area_of_expertise: '',
    url: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        publisher_id: initialData.publisher_id || '',
        credibility_rating: initialData.credibility_rating || 50,
        area_of_expertise: initialData.area_of_expertise || '',
        url: initialData.url || ''
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'credibility_rating' ? Number(value) : 
               name === 'publisher_id' && value ? Number(value) : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow-sm">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Author Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>
      
      <div>
        <label htmlFor="url" className="block text-sm font-medium text-gray-700">Author URL</label>
        <input
          type="url"
          id="url"
          name="url"
          value={formData.url}
          onChange={handleChange}
          placeholder="https://example.com/author"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>
      
      <div>
        <label htmlFor="publisher_id" className="block text-sm font-medium text-gray-700">Publisher</label>
        <select
          id="publisher_id"
          name="publisher_id"
          value={formData.publisher_id}
          onChange={handleChange}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
        >
          <option value="">None / Independent</option>
          {publishers.map(publisher => (
            <option key={publisher.id} value={publisher.id}>{publisher.name}</option>
          ))}
        </select>
      </div>
      
      <div>
        <label htmlFor="credibility_rating" className="block text-sm font-medium text-gray-700">
          Credibility Rating (0-100)
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="range"
            id="credibility_rating"
            name="credibility_rating"
            min="0"
            max="100"
            value={formData.credibility_rating}
            onChange={handleChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-sm font-medium text-gray-700">{formData.credibility_rating}</span>
        </div>
      </div>
      
      <div>
        <label htmlFor="area_of_expertise" className="block text-sm font-medium text-gray-700">Area of Expertise</label>
        <textarea
          id="area_of_expertise"
          name="area_of_expertise"
          value={formData.area_of_expertise}
          onChange={handleChange}
          rows="3"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          placeholder="Describe the author's areas of expertise or specialization..."
        />
      </div>
      
      <div className="flex justify-end space-x-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {initialData ? 'Update Author' : 'Add Author'}
        </button>
      </div>
    </form>
  );
};

const ArticleAdminTab = () => {
  // State variables
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedArticleId, setExpandedArticleId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [activeSection, setActiveSection] = useState('list'); // 'list', 'add', 'publishers', 'authors', 'claims', 'schema', 'advancedEdit'
  const [publishers, setPublishers] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [dbSetupSuccess, setDbSetupSuccess] = useState(null);
  const [dbSetupLoading, setDbSetupLoading] = useState(false);
  
  // Advanced editor state
  const [advancedEditArticleId, setAdvancedEditArticleId] = useState(null);
  
  // Claims-related state
  const [selectedArticleForClaims, setSelectedArticleForClaims] = useState(null);
  const [articleClaims, setArticleClaims] = useState([]);
  const [articleContent, setArticleContent] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10); // Fixed at 10 items per page
  const [totalArticles, setTotalArticles] = useState(0);

  // Publishers and Authors forms state
  const [showPublisherForm, setShowPublisherForm] = useState(false);
  const [editingPublisher, setEditingPublisher] = useState(null);
  const [showAuthorForm, setShowAuthorForm] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);

  // Initial data loading
  useEffect(() => {
    loadArticles();
    loadPublishers();
    loadAuthors();
  }, []);

  // Load articles with pagination
  const loadArticles = async (page = currentPage) => {
    try {
      setLoading(true);
      setError(null);
      
      // Force admin=true for this component since it's only used in Admin section
      const response = await fetch(`/api/articles/list?page=${page}&pageSize=${pageSize}&admin=true`);
      console.log(`Article admin tab loading articles with page=${page}, pageSize=${pageSize}, admin=true`);
      if (!response.ok) {
        throw new Error('Failed to fetch articles');
      }
      
      const data = await response.json();
      
      // Update articles state
      setArticles(data.articles || []);
      
      // Update pagination state if provided
      if (data.pagination) {
        setTotalPages(data.pagination.totalPages || 1);
        setCurrentPage(data.pagination.currentPage || 1);
        setTotalArticles(data.pagination.totalArticles || 0);
      }
    } catch (error) {
      console.error("Error loading articles:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load publishers
  const loadPublishers = async () => {
    try {
      // Use relative URL to avoid domain issues
      console.log("Fetching publishers...");
      const response = await fetch('/api/admin/publishers');
      console.log("Publishers response:", response);
      if (!response.ok) {
        throw new Error('Failed to fetch publishers');
      }
      
      const data = await response.json();
      console.log("Publishers data:", data);
      setPublishers(data.publishers || []);
    } catch (error) {
      console.error("Error loading publishers:", error);
    }
  };

  // Load authors
  const loadAuthors = async () => {
    try {
      // Use relative URL to avoid domain issues
      console.log("Fetching authors...");
      const response = await fetch('/api/admin/authors');
      console.log("Authors response:", response);
      if (!response.ok) {
        throw new Error('Failed to fetch authors');
      }
      
      const data = await response.json();
      console.log("Authors data:", data);
      setAuthors(data.authors || []);
    } catch (error) {
      console.error("Error loading authors:", error);
    }
  };
  
  // Load article content and claims for the claims section
  const loadArticleForClaims = async (articleId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch article details
      const articleResponse = await fetch(`/api/articles/${articleId}`);
      if (!articleResponse.ok) {
        throw new Error('Failed to fetch article details');
      }
      
      const articleData = await articleResponse.json();
      
      if (!articleData.article) {
        throw new Error('Article data is malformed. No article object found in response.');
      }
      
      setSelectedArticleForClaims(articleData.article);
      
      // Get content from either the article.content field or the separate content object
      let content = null;
      if (articleData.article.content) {
        content = articleData.article.content;
      } else if (articleData.content && articleData.content.text) {
        content = articleData.content.text;
      } else {
        console.warn("No content found for article. Using empty string.");
        content = "";
      }
      
      setArticleContent(content);
      
      // Fetch existing claims
      const claimsResponse = await fetch(`/api/articles/${articleId}/claims`);
      if (!claimsResponse.ok) {
        throw new Error('Failed to fetch article claims');
      }
      
      const claimsData = await claimsResponse.json();
      setArticleClaims(claimsData.claims || []);
      
    } catch (error) {
      console.error("Error loading article for claims:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle claims change
  const handleClaimsChange = (claims) => {
    setArticleClaims(claims);
  };

  // Toggle article expansion
  const toggleExpandArticle = (articleId) => {
    setExpandedArticleId(expandedArticleId === articleId ? null : articleId);
  };

  // Handle article submission
  const handleSubmitArticle = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = editingArticleId 
        ? `/api/articles/${editingArticleId}` 
        : '/api/articles/submit';
      
      const method = editingArticleId ? 'PUT' : 'POST';
      
      const response = await fetch(endpoint, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit article');
      }
      
      const result = await response.json();
      
      // If we're creating a new article, automatically analyze it
      if (!editingArticleId && result.id) {
        try {
          // Show a toast to indicate analysis is starting
          window.toast.info('Analysis Started', 'Article saved. Starting Claude analysis...', 5000);
          
          // Call the analyze endpoint with the new article ID
          await handleAnalyzeArticle(result.id);
        } catch (analysisError) {
          console.error("Error during automatic analysis:", analysisError);
          // Show error toast
          window.toast.error('Analysis Failed', analysisError.message, 10000);
          // Continue with normal flow even if analysis fails
        }
      }
      
      // Reset form state
      setEditingArticleId(null);
      setActiveSection('list');
      
      // Reload articles to show the new/updated one
      await loadArticles();
      
      return result;
    } catch (error) {
      console.error("Error submitting article:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handle article deletion
  const handleDeleteArticle = async (articleId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/articles/${articleId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete article');
      }
      
      // Update local state
      setArticles(articles.filter(article => article.id !== articleId));
      setShowDeleteConfirm(null);
      
      // Reset expanded state if needed
      if (expandedArticleId === articleId) {
        setExpandedArticleId(null);
      }
    } catch (error) {
      console.error("Error deleting article:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle database setup
  const setupDatabase = async () => {
    try {
      setDbSetupLoading(true);
      setError(null);
      setDbSetupSuccess(null);
      
      const response = await fetch('/api/admin/create-article-relationship-tables', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || result.details || 'Failed to set up database tables');
      }
      
      setDbSetupSuccess('Database tables created successfully!');
      
      // Reload authors and publishers
      await loadAuthors();
      await loadPublishers();
      
    } catch (error) {
      console.error("Error setting up database:", error);
      setError(error.message);
    } finally {
      setDbSetupLoading(false);
    }
  };

  // Set up author and publisher fields
  const setupAuthorFields = async () => {
    try {
      setDbSetupLoading(true);
      setError(null);
      
      const response = await fetch('/api/admin/add-author-expertise', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.details || 'Failed to set up author fields');
      }
      
      const result = await response.json();
      setDbSetupSuccess(`Author fields updated: ${result.message}`);
      
      // Reload authors
      await loadAuthors();
      
    } catch (error) {
      console.error("Error setting up author fields:", error);
      setError(error.message);
    } finally {
      setDbSetupLoading(false);
    }
  };

  // Handle edit article - open the form-based editor
  const handleEditArticle = (articleId) => {
    const articleToEdit = articles.find(a => a.id === articleId);
    if (articleToEdit) {
      setEditingArticleId(articleId);
      setActiveSection('add');
    }
  };

  // Analyze article (send to Claude)
  const handleAnalyzeArticle = async (articleId) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Starting analysis process for article ID: ${articleId}`);
      
      // First verify the article has content
      const contentCheckResponse = await fetch(`/api/articles/${articleId}`);
      
      if (!contentCheckResponse.ok) {
        console.error(`Error fetching article data: Status ${contentCheckResponse.status}`);
        throw new Error('Failed to fetch article data before analysis');
      }
      
      const articleData = await contentCheckResponse.json();
      console.log('Article data received:', articleData);
      
      // Check if we have the expected structure
      if (!articleData.article) {
        console.error('Article data missing article object:', articleData);
        throw new Error('Article data is malformed. No article object in response.');
      }
      
      // Verify the article has content (check all possible content locations)
      const hasContent = 
        (articleData.article && articleData.article.content) || 
        (articleData.content && articleData.content.text) ||
        (articleData.article_content && articleData.article_content.length > 0);
      
      if (!hasContent) {
        console.error('Article has no content:', articleData);
        throw new Error('Article has no content. Please ensure content was properly extracted or entered manually.');
      }
      
      console.log('Content check passed, proceeding with analysis');
      
      // Proceed with analysis - use a try/catch specifically for the API call
      let response;
      try {
        response = await fetch(`/api/articles/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ article_id: articleId }),
        });
      } catch (fetchError) {
        console.error('Network error during analysis API call:', fetchError);
        throw new Error(`Network error: ${fetchError.message}`);
      }
      
      // Log the raw response
      console.log(`Analysis API response status: ${response.status}`);
      
      // Handle non-OK responses
      if (!response.ok) {
        let errorDetail = 'Unknown error';
        let errorData = {};
        
        try {
          errorData = await response.json();
          console.error('Error response from analyze API:', errorData);
          errorDetail = errorData.details || errorData.error || 'Unknown server error';
        } catch (jsonError) {
          // Could not parse the error response as JSON
          console.error(`Could not parse error response: ${jsonError.message}`);
          errorDetail = `Server error (status ${response.status})`;
        }
        
        // Create a more detailed error
        const errorMessage = `Analysis failed: ${errorDetail}`;
        console.error(errorMessage, errorData);
        throw new Error(errorMessage);
      }
      
      // Try to parse the successful response
      let result;
      try {
        result = await response.json();
        console.log('Analysis result received:', result);
      } catch (parseError) {
        console.error('Error parsing analysis response:', parseError);
        throw new Error(`Error parsing analysis response: ${parseError.message}`);
      }
      
      // Reload articles to show updated analysis
      await loadArticles();
      
      window.toast.success('Analysis Complete', 'Article was successfully analyzed by Claude', 5000);
      return result;
    } catch (error) {
      console.error("Error analyzing article:", error);
      setError(`Analysis failed: ${error.message}`);
      window.toast.error('Analysis Failed', `Error: ${error.message}`, 8000);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get accuracy badge
  const getAccuracyBadge = (score) => {
    if (!score && score !== 0) return null;
    
    let bgColor, textColor;
    if (score < 30) {
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
    } else if (score < 60) {
      bgColor = 'bg-orange-100';
      textColor = 'text-orange-800';
    } else {
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
    }
    
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {score}/100
      </span>
    );
  };
  
  // Get claims badge
  const getClaimsBadge = (article) => {
    // If has_claims is a proper flag we can use it
    if (article.has_claims) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 ml-2">
          Claims
        </span>
      );
    }
    return null;
  };

  // Publisher Form Handlers
  const handleAddPublisher = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/admin/publishers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add publisher');
      }
      
      await loadPublishers();
      setShowPublisherForm(false);
      window.toast.success('Success', 'Publisher added successfully', 3000);
    } catch (error) {
      console.error("Error adding publisher:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdatePublisher = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      const urlToUse = `/api/admin/publishers/${editingPublisher.id}`;
      console.log("Updating publisher with URL:", urlToUse);
      console.log("Update data:", formData);
      
      const response = await fetch(urlToUse, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      console.log("Publisher update response:", response);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error data:", errorData);
        throw new Error(errorData.error || 'Failed to update publisher');
      }
      
      await loadPublishers();
      setEditingPublisher(null);
      window.toast.success('Success', 'Publisher updated successfully', 3000);
    } catch (error) {
      console.error("Error updating publisher:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeletePublisher = async (publisherId) => {
    if (!confirm(`Are you sure you want to delete this publisher?`)) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/admin/publishers/${publisherId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete publisher');
      }
      
      await loadPublishers();
      window.toast.success('Success', 'Publisher deleted successfully', 3000);
    } catch (error) {
      console.error("Error deleting publisher:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Author Form Handlers
  const handleAddAuthor = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/admin/authors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add author');
      }
      
      await loadAuthors();
      setShowAuthorForm(false);
      window.toast.success('Success', 'Author added successfully', 3000);
    } catch (error) {
      console.error("Error adding author:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateAuthor = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use a completely different endpoint to avoid routing issues
      const urlToUse = `/api/admin/update-author`;
      console.log("Using dedicated author update endpoint:", urlToUse);
      console.log("Author ID:", editingAuthor.id);
      console.log("Author update data:", formData);
      
      // Add the ID to the request body instead of the URL
      const requestData = {
        ...formData,
        id: editingAuthor.id
      };
      
      const response = await fetch(urlToUse, {
        method: 'POST', // Use POST to avoid any method routing issues
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      console.log("Author update response:", response);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Author update error data:", errorData);
        throw new Error(errorData.error || 'Failed to update author');
      }
      
      await loadAuthors();
      setEditingAuthor(null);
      window.toast.success('Success', 'Author updated successfully', 3000);
    } catch (error) {
      console.error("Error updating author:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteAuthor = async (authorId) => {
    if (!confirm(`Are you sure you want to delete this author?`)) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/admin/authors/${authorId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete author');
      }
      
      await loadAuthors();
      window.toast.success('Success', 'Author deleted successfully', 3000);
    } catch (error) {
      console.error("Error deleting author:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading && !articles.length) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="px-6 py-4">
      {/* Admin Editor Modal removed - now using in-place editing */}
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Section tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6">
          <button
            className={`${
              activeSection === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            onClick={() => setActiveSection('list')}
          >
            Articles List
          </button>
          <button
            className={`${
              activeSection === 'workflow'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            onClick={() => {
              setActiveSection('workflow');
            }}
          >
            Add New Article (All-in-One)
          </button>
          <button
            className={`${
              activeSection === 'add'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            onClick={() => {
              setEditingArticleId(null); // Reset editing state when adding new
              setActiveSection('add');
            }}
          >
            {editingArticleId ? 'Basic Form Edit' : 'Add New Article'}
          </button>
          <button
            className={`${
              activeSection === 'claims'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            onClick={() => {
              setSelectedArticleForClaims(null);
              setArticleClaims([]);
              setArticleContent('');
              setActiveSection('claims');
            }}
          >
            Extract Claims
          </button>
          <button
            className={`${
              activeSection === 'publishers'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            onClick={() => setActiveSection('publishers')}
          >
            Publishers
          </button>
          <button
            className={`${
              activeSection === 'authors'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            onClick={() => setActiveSection('authors')}
          >
            Authors
          </button>
          <button
            className={`${
              activeSection === 'schema'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            onClick={() => setActiveSection('schema')}
          >
            <Database className="h-4 w-4 mr-1" />
            Database Schema
          </button>
        </nav>
      </div>

      {/* Database setup success message */}
      {dbSetupSuccess && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">{dbSetupSuccess}</p>
            </div>
          </div>
        </div>
      )}

      {/* All-in-One Workflow Section */}
      {activeSection === 'workflow' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">All-in-One Article Workflow</h2>
            <div className="text-sm text-gray-500">
              Submit, extract claims, analyze, and publish - all on one page!
            </div>
          </div>
          
          <ArticleWorkflow 
            publishers={publishers} 
            authors={authors} 
          />
        </div>
      )}
      
      {/* Articles List Section */}
      {activeSection === 'list' && (
        <div>
          <div className="mb-4 flex justify-between">
            <h2 className="text-lg font-medium text-gray-900">Analyzed Articles</h2>
            <div className="flex space-x-2">
              <button
                onClick={setupDatabase}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={dbSetupLoading}
              >
                {dbSetupLoading ? 'Setting Up...' : 'Setup Database Tables'}
              </button>
              <button
                onClick={() => {
                  setActiveSection('workflow');
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Add New Article (All-in-One)
              </button>
            </div>
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No articles available. Add your first article to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Publisher
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {articles.map(article => (
                    <React.Fragment key={article.id}>
                      <tr>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <button 
                              className="text-gray-400 hover:text-gray-500 mr-2"
                              onClick={() => toggleExpandArticle(article.id)}
                            >
                              {expandedArticleId === article.id ? (
                                <ChevronUp className="h-5 w-5" />
                              ) : (
                                <ChevronDown className="h-5 w-5" />
                              )}
                            </button>
                            <span className="text-sm text-gray-900 truncate max-w-xs">
                              {article.title}
                              {getClaimsBadge(article)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {article.publisher_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {article.publication_date || article.analysis_date?.split('T')[0]}
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${article.status === 'Published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>                             
                              {article.status || 'Pending Review'}
                            </span>
                            <button 
                              className="ml-2 text-xs text-blue-600 hover:text-blue-900"
                              onClick={async () => {
                                try {
                                  const newStatus = article.status === 'Published' ? 'Pending Review' : 'Published';
                                  await fetch(`/api/articles/${article.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      ...article,
                                      status: newStatus
                                    }),
                                  });
                                  
                                  // Update local state
                                  const updatedArticles = articles.map(a => 
                                    a.id === article.id ? {...a, status: newStatus} : a
                                  );
                                  setArticles(updatedArticles);
                                  
                                  window.toast.success('Success', `Article status updated to: ${newStatus}`, 3000);
                                } catch (error) {
                                  console.error('Error updating status:', error);
                                  window.toast.error('Error', 'Failed to update article status', 3000);
                                }
                              }}
                            >
                              Toggle Status
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getAccuracyBadge(article.overall_rating)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 hover:bg-blue-200 inline-flex items-center"
                              onClick={() => {
                                setAdvancedEditArticleId(article.id);
                                setActiveSection('advancedEdit');
                              }}
                              title="Edit article with advanced in-place editor"
                            >
                              <Edit className="h-4 w-4 mr-1" /> Edit Article
                            </button>
                            <button
                              className="text-red-600 hover:text-red-900"
                              onClick={() => setShowDeleteConfirm(article.id)}
                              title="Delete article"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                            <a
                              href={`/a/${article.id}/${article.title ? article.title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').substring(0, 50) : 'article'}.html`}
                              className="text-gray-600 hover:text-gray-900"
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View article analysis"
                            >
                              <ExternalLink className="h-5 w-5" />
                            </a>
                            <button
                              className="text-purple-600 hover:text-purple-900"
                              onClick={() => {
                                loadArticleForClaims(article.id);
                                setActiveSection('claims');
                              }}
                              title="Extract claims"
                            >
                              <FileText className="h-5 w-5" />
                            </button>
                            {!article.overall_rating && (
                              <button
                                className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                                onClick={() => handleAnalyzeArticle(article.id)}
                                title="Analyze with Claude"
                              >
                                Analyze
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded row for article details */}
                      {expandedArticleId === article.id && (
                        <tr>
                          <td colSpan="5" className="px-6 py-4 bg-gray-50">
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="font-medium">URL:</span>{' '}
                                <a 
                                  href={article.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  {article.url}
                                </a>
                              </div>
                              {article.summary && (
                                <div>
                                  <span className="font-medium">Summary:</span> {article.summary}
                                </div>
                              )}
                              {article.overall_rating && (
                                <div className="space-y-1">
                                  <div className="font-medium">Ratings:</div>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                    <div>Constitutional Accuracy: {getAccuracyBadge(article.constitutional_accuracy_score)}</div>
                                    <div>Source Credibility: {getAccuracyBadge(article.source_credibility_score)}</div>
                                    <div>Rhetoric Score: {getAccuracyBadge(article.rhetoric_score)}</div>
                                  </div>
                                </div>
                              )}
                              {article.primary_fallacies && (
                                <div>
                                  <span className="font-medium">Primary Fallacies:</span>{' '}
                                  {JSON.parse(article.primary_fallacies || '[]').join(', ')}
                                </div>
                              )}
                              {article.democratic_pillars_threatened && (
                                <div>
                                  <span className="font-medium">Democratic Pillars Threatened:</span>{' '}
                                  {JSON.parse(article.democratic_pillars_threatened || '[]').join(', ')}
                                </div>
                              )}
                              <div>
                                <span className="font-medium">Claims Status:</span>{' '}
                                {article.has_claims ? (
                                  <span className="text-green-600">Article has extracted claims</span>
                                ) : (
                                  <span className="text-gray-500">No claims extracted yet</span>
                                )}
                                {!article.has_claims && (
                                  <button 
                                    className="ml-2 text-sm text-indigo-600 hover:text-indigo-900"
                                    onClick={() => {
                                      loadArticleForClaims(article.id);
                                      setActiveSection('claims');
                                    }}
                                  >
                                    Extract Claims
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      
                      {/* Delete confirmation row */}
                      {showDeleteConfirm === article.id && (
                        <tr>
                          <td colSpan="5" className="px-6 py-4 bg-red-50">
                            <div className="flex justify-between items-center">
                              <div>
                                <h3 className="text-sm font-medium text-red-800">Are you sure you want to delete this article?</h3>
                                <p className="text-xs text-red-700 mt-1">This action cannot be undone.</p>
                              </div>
                              <div className="flex space-x-2">
                                <button 
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                                  onClick={() => handleDeleteArticle(article.id)}
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Yes, delete
                                </button>
                                <button 
                                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                  onClick={() => setShowDeleteConfirm(null)}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="flex-1 flex justify-between items-center">
                <button
                  onClick={() => loadArticles(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 border ${
                    currentPage === 1 ? 'border-gray-300 bg-gray-100 text-gray-400' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  } text-sm font-medium rounded-md`}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => loadArticles(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-4 py-2 border ${
                    currentPage === totalPages ? 'border-gray-300 bg-gray-100 text-gray-400' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  } text-sm font-medium rounded-md`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Article Section */}
      {activeSection === 'add' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">
              {editingArticleId ? 'Basic Article Edit Form' : 'Add New Article'}
            </h2>
            {editingArticleId && (
              <button 
                onClick={() => {
                  setEditingArticleId(null);
                  setActiveSection('list');
                }}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel Edit
              </button>
            )}
          </div>
          
          <ArticleSubmissionForm
            onSubmit={handleSubmitArticle}
            initialData={editingArticleId ? articles.find(a => a.id === editingArticleId) : null}
            isEditing={!!editingArticleId}
            publishers={publishers}
            authors={authors}
          />
        </div>
      )}

      {/* Publishers Section */}
      {activeSection === 'publishers' && (
        <div>
          <div className="mb-4 flex justify-between">
            <h2 className="text-lg font-medium text-gray-900">Publishers</h2>
            <div className="flex space-x-2">
              <button
                onClick={setupDatabase}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Setup/Verify Schema
              </button>
              {!showPublisherForm && !editingPublisher && (
                <button
                  onClick={() => setShowPublisherForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add Publisher
                </button>
              )}
            </div>
          </div>

          {/* Publisher Form */}
          {(showPublisherForm || editingPublisher) && (
            <div className="mb-6">
              <PublisherForm 
                initialData={editingPublisher}
                onSubmit={editingPublisher ? handleUpdatePublisher : handleAddPublisher}
                onCancel={() => {
                  setShowPublisherForm(false);
                  setEditingPublisher(null);
                }}
              />
            </div>
          )}

          {publishers.length === 0 && !showPublisherForm ? (
            <div className="text-center py-8 text-gray-500">
              <p>No publishers available. Add your first publisher to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      URL
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Credibility Rating
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {publishers.map(publisher => (
                    <tr key={publisher.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {publisher.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {publisher.url ? (
                          <a href={publisher.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                            <span className="truncate max-w-xs">{publisher.url}</span>
                            <ExternalLink className="h-3 w-3 ml-1 inline" />
                          </a>
                        ) : (
                          <span className="text-gray-400">Not set</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getAccuracyBadge(publisher.credibility_rating)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => {
                              setShowPublisherForm(false);
                              setEditingPublisher(publisher);
                            }}
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => handleDeletePublisher(publisher.id)}
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Claims Section */}
      {activeSection === 'claims' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">
              Extract Claims from Articles
            </h2>
            {selectedArticleForClaims && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">
                  Selected: <span className="font-medium">{selectedArticleForClaims.title}</span>
                </span>
                <button
                  onClick={() => {
                    setSelectedArticleForClaims(null);
                    setArticleClaims([]);
                    setArticleContent('');
                  }}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Change Article
                </button>
              </div>
            )}
          </div>
          
          {!selectedArticleForClaims ? (
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h3 className="text-lg font-medium mb-4">Select an Article</h3>
              <p className="mb-4 text-gray-700">
                Choose an article from the list to extract and analyze its claims:
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Publisher
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {articles.map(article => (
                      <tr key={article.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {article.title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {article.publisher_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            className="text-indigo-600 hover:text-indigo-900"
                            onClick={() => loadArticleForClaims(article.id)}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <ArticleClaimExtractor
              articleId={selectedArticleForClaims.id}
              articleContent={articleContent}
              onClaimsChange={handleClaimsChange}
              existingClaims={articleClaims}
            />
          )}
        </div>
      )}

      {/* Authors Section */}
      {activeSection === 'authors' && (
        <div>
          <div className="mb-4 flex justify-between">
            <h2 className="text-lg font-medium text-gray-900">Authors</h2>
            <div className="flex space-x-2">
              <button
                onClick={setupAuthorFields}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Setup/Verify Schema
              </button>
              {!showAuthorForm && !editingAuthor && (
                <button
                  onClick={() => setShowAuthorForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add Author
                </button>
              )}
            </div>
          </div>

          {/* Author Form */}
          {(showAuthorForm || editingAuthor) && (
            <div className="mb-6">
              <AuthorForm 
                initialData={editingAuthor}
                publishers={publishers}
                onSubmit={editingAuthor ? handleUpdateAuthor : handleAddAuthor}
                onCancel={() => {
                  setShowAuthorForm(false);
                  setEditingAuthor(null);
                }}
              />
            </div>
          )}

          {authors.length === 0 && !showAuthorForm ? (
            <div className="text-center py-8 text-gray-500">
              <p>No authors available. Add your first author to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      URL
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Publisher
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Credibility Rating
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {authors.map(author => (
                    <tr key={author.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {author.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {author.url ? (
                          <a href={author.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                            <span className="truncate max-w-xs">{author.url}</span>
                            <ExternalLink className="h-3 w-3 ml-1 inline" />
                          </a>
                        ) : (
                          <span className="text-gray-400">Not set</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {author.publisher_name || 'None'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getAccuracyBadge(author.credibility_rating)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => {
                              setShowAuthorForm(false);
                              setEditingAuthor(author);
                            }}
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => handleDeleteAuthor(author.id)}
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Database Schema Section */}
      {activeSection === 'schema' && (
        <ArticleSchemaManager />
      )}
      
      {/* Advanced In-Place Editor Section */}
      {activeSection === 'advancedEdit' && advancedEditArticleId && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">
              Advanced In-Place Editor
            </h2>
            <button 
              onClick={() => {
                setAdvancedEditArticleId(null);
                setActiveSection('list');
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Articles List
            </button>
          </div>
          
          {/* Here we embed the ArticleDetailView component directly */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden border">
            <ArticleDetailView 
              articleId={advancedEditArticleId}
              isAdmin={true} 
              inAdminMode={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleAdminTab;